/**
 * Rules fired from the site's own code.
 *
 * A "code" rule has no URL, element or form to match: the site calls
 * `TheSocialTap.track("Lead", { rule: "<rule id>" })` at the moment it knows
 * the conversion happened. The rule then decides what a URL rule decides:
 * the event name, the platforms, the value and once per visitor.
 */

import { buildCustomData } from "./custom-params.js";
import { DEFAULT_CURRENCY } from "./constants.js";
import type { EventData, TrackingRule } from "./types.js";

/** The track() parameter that names the rule. Never forwarded as custom data. */
export const RULE_PARAM = "rule";

export function ruleReference(valueOrParams: unknown): string | null {
  if (!valueOrParams || typeof valueOrParams !== "object") return null;
  const reference = (valueOrParams as Record<string, unknown>)[RULE_PARAM];
  if (typeof reference !== "string" || reference === "") return null;
  return reference;
}

/** Only enabled rules reach the config, so a disabled rule is not found either. */
export function findCodeRule(rules: readonly TrackingRule[] | undefined, reference: string): TrackingRule | null {
  if (!rules) return null;
  for (const rule of rules) {
    if (rule.triggerType === "code" && rule.id === reference) return rule;
  }
  return null;
}

/**
 * The event a code rule sends. A value passed in code beats the rule's fixed value.
 *
 * An eventId passed in code is kept. Ingest dedupes on (website, eventId) and
 * the pixel and our CAPI copy share it, so a site that fires the same
 * conversion twice (a reloaded confirmation step) books it once. It does NOT
 * dedupe against a /collect/hook server event: that path mints its own id.
 */
export function codeRuleEvent(rule: TrackingRule, params: Record<string, unknown>): EventData {
  const data: EventData = { eventName: rule.eventName, trackingRuleId: rule.id };
  if (typeof params.eventId === "string" && params.eventId !== "") data.eventId = params.eventId;
  data.eventValue = rule.eventValue;
  if (typeof params.value === "number") data.eventValue = params.value;
  data.currency = rule.currency || DEFAULT_CURRENCY;
  if (typeof params.currency === "string" && params.currency !== "") data.currency = params.currency;
  const customData = buildCustomData(params);
  if (customData) data.customData = customData;
  return data;
}
